<template>
  <div class="modal-overlay z900 mm-backdrop" @click.self="$emit('close')">
    <div class="mm-panel" role="dialog" aria-modal="true" aria-label="Your active skill modifiers">
      <button class="mm-close" @click="$emit('close')" aria-label="Close">×</button>
      <h2 class="mm-title">⚡ {{ handle }} — skill modifiers</h2>
      <p class="mm-sub">How your owned !buyrandom skills are nudging your next character roll.</p>

      <div v-if="!loaded" class="mm-state">Loading your skills…</div>

      <template v-else-if="hasData">
        <template v-if="mods.length">
          <div class="mm-sec">⚡ Active roll modifiers <span class="mm-sec-sub">how owned skills nudge your character roll</span></div>
          <div class="mm-mods">
            <span v-for="m in mods" :key="m.token" class="mm-mod"
                  :class="{ bonus: m.isBonus, cantuse: m.mod.cannotUse, hidden: m.hidden }">
              <b>{{ m.name }}</b> <span class="mm-mod-v">{{ m.modText }}</span>
            </span>
          </div>
        </template>

        <template v-if="cb">
          <div class="mm-sec">💰 EXP you can earn <span class="mm-sec-sub">bonuses for how you enter</span></div>
          <p class="mm-bonus">
            <span class="mm-amt">+{{ cb.amount }} EXP</span> — enter with no parameters, or as one of:
            <span class="mm-classes">{{ cb.classes.join(', ') }}</span>
          </p>
          <p v-if="skillBonus" class="mm-bonus">
            <span class="mm-amt">+2 EXP</span> — use <b>{{ skillBonusName }}</b> in battle.
          </p>
        </template>

        <template v-if="entries">
          <div class="mm-sec mm-sec-toggle" @click="entriesExpanded = !entriesExpanded">
            🎮 Suggested entry <span class="mm-sec-sub">priority-aware !fight commands · {{ gil.toLocaleString() }}G</span>
            <span class="mm-toggle">{{ entriesExpanded ? '▾ less' : '▸ all options' }}</span>
          </div>

          <!-- Class picker (remembered), plus a one-time "set up My entry". -->
          <div class="mm-classpick-row">
            <div class="mm-classpick">Suggest for:
              <button class="mm-class-btn" :title="'Pick a class, or a monster' + (bonusSet.size ? ' (✦ = earns your class bonus)' : '')" @click="classPickerOpen = true">
                <img v-if="pickerCurrent.icon" class="mm-class-btn-img" :src="pickerCurrent.icon" alt="" @error="$event.target.style.visibility = 'hidden'">
                <span v-else class="mm-class-btn-emoji">{{ pickerCurrent.emoji }}</span>
                <span class="mm-class-btn-name">{{ pickerCurrent.label }}</span>
                <span v-if="pickerCurrent.bonus" class="mm-class-btn-bonus" title="Earns your class bonus">✦ bonus</span>
                <span class="mm-class-btn-caret">▾</span>
              </button>
              <span v-if="chosenClass" class="mm-remembered" title="Opens on this class next time">⭐ remembered</span>
            </div>
            <button v-if="chosenClass !== 'MONSTER' && !entrySetUp" class="mm-custom-toggle" @click="setUpMyEntry">⭐ Set up My entry</button>
          </div>

          <ClassPickerModal v-if="classPickerOpen" :classes="pickerTiles" :current="chosenClass || AUTO_VAL" :current-gender="pickedGender || ''"
                            heading="🎮 Suggest an entry for" @pick="onPickClass" @close="classPickerOpen = false" />

          <!-- ⭐ My entry: your rules, remembered, as one command to copy. -->
          <div v-if="showMyEntry" class="mm-entry mm-myentry" :class="{ poor: !customEntry.affordable }">
            <div class="mm-entry-head">
              <span class="mm-entry-label">⭐ My entry <span class="mm-myentry-for">· {{ myEntryFor }}</span></span>
              <span class="mm-entry-meta">~{{ customEntry.priority }} priority · {{ customEntry.cost ? customEntry.cost + 'G' : 'free' }}<span v-if="!customEntry.affordable" class="mm-entry-warn"> · need more gil</span></span>
            </div>
            <div class="mm-cmd-row">
              <code class="mm-cmd" @click="copy(customEntry.command)" title="Click to copy">{{ customEntry.command }}</code>
              <button class="mm-copy" @click="copy(customEntry.command)">{{ copied === customEntry.command ? '✓ copied' : '📋' }}</button>
            </div>

            <div class="mm-rules">
              <span class="mm-rule">
                <button class="mm-ggh-toggle" :class="{ on: rules.forceBest }" role="switch" :aria-checked="rules.forceBest ? 'true' : 'false'" @click="toggleRule('forceBest')">
                  <span class="mm-ggh-box">{{ rules.forceBest ? '✓' : '' }}</span> Force my best skill
                </button>
                <span v-if="rules.forceBest" class="mm-pool" role="radiogroup" aria-label="Which skills">
                  <button class="mm-pool-opt" :class="{ on: rules.pool === 'usable' }" role="radio" :aria-checked="rules.pool === 'usable' ? 'true' : 'false'" title="Any skill you can still force (under 5 tildes), even if recent use costs you EXP or priority" @click="setPool('usable')">any usable</button>
                  <button class="mm-pool-opt" :class="{ on: rules.pool === 'offcd' }" role="radio" :aria-checked="rules.pool === 'offcd' ? 'true' : 'false'" title="Only skills with no reroll tildes, so you keep full EXP. An uncommon or rare one still costs priority to force: worth it when the bracket isn't crowded" @click="setPool('offcd')">off cooldown</button>
                  <button class="mm-pool-opt" :class="{ on: rules.pool === 'nopenalty' }" role="radio" :aria-checked="rules.pool === 'nopenalty' ? 'true' : 'false'" title="Only skills with no reroll tildes that also cost no priority to force: common skills (a common skill bonus counts)" @click="setPool('nopenalty')">no penalty</button>
                </span>
              </span>
              <button v-if="ownedHidden.length" class="mm-ggh-toggle" :class="{ on: saveHidden }" role="switch" :aria-checked="saveHidden ? 'true' : 'false'"
                      :title="'Never suggest forcing ' + orList(ownedHidden)" @click="toggleHidden">
                <span class="mm-ggh-box">{{ saveHidden ? '✓' : '' }}</span> Save my hidden skills
              </button>
              <button class="mm-ggh-toggle" :class="{ on: rules.banWorst }" role="switch" :aria-checked="rules.banWorst ? 'true' : 'false'" @click="toggleRule('banWorst')">
                <span class="mm-ggh-box">{{ rules.banWorst ? '✓' : '' }}</span> Ban the worst roll
              </button>
              <button class="mm-ggh-toggle" :class="{ on: excludeGgh }" role="switch" :aria-checked="excludeGgh ? 'true' : 'false'" @click="toggleGgh">
                <span class="mm-ggh-box">{{ excludeGgh ? '✓' : '' }}</span> Exclude GilgameHeart
              </button>
              <button class="mm-ggh-toggle" :class="{ on: rules.respectQuest && !questWhyNot }" role="switch" :aria-checked="rules.respectQuest ? 'true' : 'false'"
                      :disabled="!!questWhyNot && !rules.respectQuest" :title="questWhyNot || 'Enter as something your ' + questGoal.name + ' still needs'" @click="toggleRule('respectQuest')">
                <span class="mm-ggh-box">{{ rules.respectQuest ? '✓' : '' }}</span> Respect my quest<template v-if="questGoal"> ({{ questGoal.name }})</template>
              </button>
              <button class="mm-ggh-toggle" :class="{ on: rules.vip }" role="switch" :aria-checked="rules.vip ? 'true' : 'false'"
                      title="Enter as a class you haven't champed this season, picked for its edge on the champion you'd face" @click="toggleRule('vip')">
                <span class="mm-ggh-box">{{ rules.vip ? '✓' : '' }}</span> 🎖️ Help me with VIP quest
              </button>
            </div>

            <!-- 🎖️ The VIP Quest helper: what you still need, and the candidates against this champion. -->
            <div v-if="rules.vip" class="mm-vip">
              <div v-if="vipError" class="mm-vip-msg">{{ vipError }}</div>
              <div v-else-if="!vipHist" class="mm-vip-msg">Reading this season's titles…</div>
              <div v-else-if="!vipNeeded.length" class="mm-vip-msg">🎉 You've champed all {{ vipTotal }} this season. Nothing left to chase!</div>
              <template v-else>
                <div class="mm-vip-head">
                  🎖️ <b>VIP Quest, Season {{ seasonCount }}</b>: {{ vipHave }}/{{ vipTotal }} champed, {{ vipNeeded.length }} to go
                  <a href="?vip" class="mm-vip-board">board →</a>
                </div>
                <div v-if="vipChampText" class="mm-vip-champ">vs {{ vipChampText }}</div>
                <div v-else class="mm-vip-champ">No champion on this bracket to read, so the picks go by win rate alone.</div>
                <ol v-if="vipList.length" class="mm-vip-list">
                  <li v-for="c in vipList" :key="c.cls" :class="{ on: vipPick && c.cls === vipPick.cls }" @click="pickVip(c.cls)">
                    <span class="mm-vip-name">{{ c.cls }}<template v-if="c.skill"> + {{ c.skill.name }}</template></span>
                    <span v-if="c.gated" class="mm-vip-tier">{{ vipTier(c.cls) }}</span>
                    <span v-if="vipSim[c.cls] != null" class="mm-vip-sim" title="The battle sim's chance against this champion, with random teammates">{{ vipSim[c.cls] }}%</span>
                    <span class="mm-vip-why">{{ vipWhy(c) }}</span>
                  </li>
                </ol>
                <p v-else class="mm-vip-msg">None of the classes you still need can be entered right now: the rest are monsters you don't own, or that are on cooldown.</p>
                <p v-if="vipGated.owned.length" class="mm-vip-note">Strong and elite monsters share a cooldown, so the ones you still need come first.</p>
                <p v-if="vipGated.cooling.length" class="mm-vip-note">
                  ⏳ On the strong/elite cooldown for now: {{ vipGated.cooling.slice(0, 6).join(', ') }}<template v-if="vipGated.cooling.length > 6"> and {{ vipGated.cooling.length - 6 }} more</template>.
                </p>
                <p v-if="vipGated.missing.length" class="mm-vip-note">
                  Strong and elite monsters you still need but don't own: {{ vipGated.missing.slice(0, 6).join(', ') }}<template v-if="vipGated.missing.length > 6"> and {{ vipGated.missing.length - 6 }} more</template>.
                </p>
                <button v-if="vipList.length && vipChampText" class="mm-vip-simbtn" :disabled="vipSimBusy"
                        title="Rough: each candidate fights with random teammates, on a flat grid since the map isn't known yet" @click="runVipSim">
                  {{ vipSimBusy ? 'Simulating…' : Object.keys(vipSim).length ? '🎲 Run the sim again' : '🎲 Sim these against the champion' }}
                </button>
              </template>
            </div>

            <ul class="mm-breakdown">
              <template v-if="customEntry.kind === 'monster'">
                <li v-if="customEntry.via === 'vip'"><b>{{ nameOfToken(customEntry.monster) }}</b> — {{ customEntry.tier === 'elite' ? 'an' : 'a' }} {{ customEntry.tier }} monster you own and haven't champed this season · {{ vipPickWhy }}. A monster entry takes no other parameters.</li>
                <li v-else><b>{{ nameOfToken(customEntry.monster) }}</b> — {{ customEntry.tier === 'elite' ? 'an' : 'a' }} {{ customEntry.tier }} monster you own that your {{ questGoal.name }} still needs. A monster entry takes no other parameters.</li>
              </template>
              <template v-else>
                <li><b>{{ classDisplay(activeClass) }}{{ customGender === 'Female' ? ' ♀' : customGender === 'Male' ? ' ♂' : '' }}</b> — {{ classBaseWRDisp }}% base win rate<template v-if="questActive"> · your {{ questGoal.name }} still needs it</template><template v-else-if="vipActive"> · you haven't champed one this season</template></li>
                <li v-if="customEntry.gender"><b>{{ customEntry.gender === 'Female' ? '♀ Female' : '♂ Male' }}</b> — {{ questGender ? 'your quest needs it' : rules.gender ? 'your pick' : 'the stronger gender' }}</li>
                <li v-if="customEntry.forced"><b>Force {{ nameOfToken(customEntry.forced) }}</b> — {{ vipSkill && customEntry.forced === vipSkill.token ? vipSkillWhy : forcePickDisp }}{{ customEntry.usesSkillBonus ? ' · your skill bonus (+5 priority, +2 EXP)' : '' }}{{ customEntry.stepText ? ' · you\'ve rerolled it (' + customEntry.stepText + ')' : '' }}</li>
                <li v-else-if="rules.forceBest">No owned skill{{ rules.pool === 'nopenalty' ? ' without a penalty' : rules.pool === 'offcd' ? ' off cooldown' : '' }} beats {{ classDisplay(activeClass) }}'s baseline, so none is forced.</li>
                <li v-if="poolNote" class="mm-pool-note">{{ poolNote }}</li>
                <li v-if="hiddenNote" class="mm-pool-note">{{ hiddenNote }}</li>
                <li v-if="customEntry.ban"><b>Ban {{ customEntry.ban }}</b> — dodges this bad roll ({{ banPickDisp }})</li>
                <li v-if="customEntry.ggh"><b>Exclude GilgameHeart</b> — +5 priority for a 500G bribe</li>
                <li v-if="customEntry.bribeSkipped">GilgameHeart bribe skipped — you're short on gil for it.</li>
                <li v-if="customEntry.dropped.length">Left out, since an entry takes four parameters at most: {{ droppedText }}.</li>
              </template>
              <li v-if="rules.respectQuest && questWhyNot" class="mm-quest-off">🎯 {{ questWhyNot }}</li>
            </ul>

            <button v-if="customEntry.kind !== 'monster'" class="mm-finetune" @click="fineTune = !fineTune">{{ fineTune ? '▾ hide picks' : '✏️ adjust picks' }}</button>
            <div v-if="fineTune && customEntry.kind !== 'monster'" class="mm-custom">
              <div class="mm-custom-head">
                <span class="mm-custom-cls">{{ classDisplay(activeClass) }}</span>
                <span class="mm-gender-toggle">
                  <button class="mm-gpill" :class="{ on: customGender === 'Male' }" :disabled="!!genderLock || !!questGender" @click="setGender('Male')">♂ Male</button>
                  <button class="mm-gpill" :class="{ on: customGender === 'Female' }" :disabled="!!genderLock || !!questGender" @click="setGender('Female')">♀ Female</button>
                  <button class="mm-gpill" :class="{ on: !customGender }" :disabled="!!genderLock || !!questGender" title="Leave the gender to the roll (saves a parameter and 100G)" @click="setGender('')">⚥ Any</button>
                </span>
              </div>

              <div class="mm-custom-sec">⚔️ Force a skill <span class="mm-sec-sub">your best owned skills for {{ classDisplay(activeClass) }} · click to swap, click again for none</span></div>
              <div v-if="customForceList.length" class="mm-pick-list">
                <button v-for="s in customForceList" :key="s.token" class="mm-pick" :class="{ on: forcePick === s.token }" @click="selectForce(s.token)">
                  <span class="mm-pick-name">{{ s.name }}</span>
                  <span class="mm-pick-meta">
                    <span class="mm-delta" :class="s.delta >= 0 ? 'pos' : 'neg'">{{ s.delta >= 0 ? '+' : '' }}{{ s.delta }}%</span>
                    <span v-if="s.rarity !== 'common'" class="mm-rar" :class="s.rarity">{{ s.rarity }} {{ s.prio }}</span>
                    <span v-if="s.stepText" class="mm-rar step" :class="{ neg: s.stepNeg }">⟲ {{ s.stepText }}</span>
                  </span>
                </button>
              </div>
              <p v-else class="mm-entry-note">You don't own any forceable skills with data for this class yet — enter as the class alone, or pick a different class.</p>

              <div class="mm-custom-sec">🚫 Ban a skill <span class="mm-sec-sub">dodge a bad roll · click to swap, click again for none</span></div>
              <div class="mm-pick-list">
                <button v-for="b in customBanList" :key="b.token" class="mm-pick" :class="{ on: banPick === b.token }" @click="selectBan(b.token)">
                  <span class="mm-pick-name">{{ b.name }}</span>
                  <span class="mm-pick-meta"><span class="mm-delta neg">{{ b.delta }}%</span></span>
                </button>
              </div>
            </div>
          </div>

          <!-- The suggestions. Once My entry is set up it takes over the collapsed view;
               "all options" still lists these under it. -->
          <template v-if="!showMyEntry || entriesExpanded">
            <!-- Suggestion tips (expanded only, to keep the collapsed view tidy). -->
            <p v-if="entriesExpanded && genderEdgeInfo && pickedGender == null" class="mm-genderedge">💡 {{ classDisplay(activeClass) }} wins ~{{ genderEdgeInfo.delta }}% more as {{ genderEdgeInfo.gender === 'Female' ? '♀ Female' : '♂ Male' }} — folded into the commands below (+100G, no priority cost).</p>
            <p v-if="entriesExpanded && worstBan" class="mm-genderedge mm-worstban">⚠️ {{ classDisplay(activeClass) }}'s worst roll: <b>{{ worstBan.skill }}</b> as a {{ worstBan.slot }} ({{ worstBan.delta }}%). <template v-if="excludeGgh">Swap <code>-gilgameheart</code> → <code>-{{ worstBan.ban }}</code> to dodge it — trades the +5 priority (but saves 500G), worth it only if you're already a lock to get in.</template><template v-else>Add <code>-{{ worstBan.ban }}</code> (+100G) to dodge it.</template></p>
            <!-- 🐉 Monster: your owned monsters ranked by win rate, minus what's on cooldown. -->
            <div v-if="chosenClass === 'MONSTER'" class="mm-monpanel">
              <div v-if="!rankedMonsters.length" class="mm-entry-note">You don't own any monsters yet — acquire one via buyrandom to enter as it.</div>
              <template v-else>
                <div class="mm-ggh-row">
                  <button class="mm-ggh-toggle" :class="{ on: monsterQuest }" role="switch" :aria-checked="monsterQuest ? 'true' : 'false'"
                          title="Only suggest monsters you still need for the Monster Quest" @click="toggleMonsterQuest">
                    <span class="mm-ggh-box">{{ monsterQuest ? '✓' : '' }}</span> 🐉 Respect Monster Quest
                  </button>
                  <span v-if="monsterQuest" class="mm-ggh-hint">{{ mqHint }}</span>
                </div>
                <p v-if="mqEmptyMsg" class="mm-entry-note mm-mon-msg">{{ mqEmptyMsg }}</p>
              </template>
            </div>

            <!-- GilgameHeart-exclude opt-out — persisted; off → the value commands drop the bribe.
                 (My entry has the same switch, so it's only shown here without it.) -->
            <div v-if="chosenClass !== 'MONSTER' && !showMyEntry" class="mm-ggh-row">
              <button class="mm-ggh-toggle" :class="{ on: excludeGgh }" role="switch" :aria-checked="excludeGgh ? 'true' : 'false'" @click="toggleGgh">
                <span class="mm-ggh-box">{{ excludeGgh ? '✓' : '' }}</span> Exclude GilgameHeart
              </button>
              <span class="mm-ggh-hint">{{ excludeGgh ? 'buys +5 priority for a 500G bribe' : 'off — commands skip the GilgameHeart bribe' }}</span>
            </div>
            <div v-if="chosenClass !== 'MONSTER' && !showMyEntry && ownedHidden.length" class="mm-ggh-row">
              <button class="mm-ggh-toggle" :class="{ on: saveHidden }" role="switch" :aria-checked="saveHidden ? 'true' : 'false'" @click="toggleHidden">
                <span class="mm-ggh-box">{{ saveHidden ? '✓' : '' }}</span> Save my hidden skills
              </button>
              <span class="mm-ggh-hint">{{ saveHidden ? "commands won't force " : 'off — commands may force ' }}{{ orList(ownedHidden) }}</span>
            </div>

            <div v-for="e in (entriesExpanded ? entries : [headlineEntry])" :key="e.key" class="mm-entry" :class="{ poor: !e.affordable }">
              <div class="mm-entry-head">
                <span class="mm-entry-label">{{ e.icon }} {{ e.label }}</span>
                <span class="mm-entry-meta">~{{ e.priority }} priority · {{ e.cost ? e.cost + 'G' : 'free' }}<span v-if="!e.affordable" class="mm-entry-warn"> · need more gil</span></span>
              </div>
              <div class="mm-cmd-row">
                <code class="mm-cmd" @click="copy(e.command)" title="Click to copy">{{ e.command }}</code>
                <button class="mm-copy" @click="copy(e.command)">{{ copied === e.command ? '✓ copied' : '📋' }}</button>
              </div>
              <div v-if="entriesExpanded || e.mon" class="mm-entry-note">{{ e.note }}</div>
            </div>
            <!-- Monsters you own but can't roll yet: always when nothing else is left, else under "all options". -->
            <template v-if="chosenClass === 'MONSTER' && (entriesExpanded || !monsterEntries.length)">
              <p v-for="b in monsterBlocked" :key="b.key" class="mm-entry-note mm-mon-msg">⏳ {{ b.text }}</p>
            </template>
            <p v-if="entriesExpanded" class="mm-note">Priority shown is the base — add +20 if you're subscribed, +40 if you were snubbed last tournament; recent admissions / recently-used skills lower it.</p>
          </template>
        </template>

        <p class="mm-note">Snub and recency adjustments aren't shown — those are computed bot-side, not in the public files.</p>
        <a :href="'?player=' + encodeURIComponent(handle) + '&tab=skills'" class="mm-link">See your full skill collection →</a>
      </template>

      <div v-else class="mm-state">
        Couldn't load your live skill data — this only works on the live site (luxor's wiznaib.us).
      </div>
    </div>
  </div>
</template>

<script>
import modalA11y from '../mixins/modalA11y.js';
import ClassPickerModal from './ClassPickerModal.vue';
import { UNITS_BASE } from '../lib/assetUrls.js';
import { classPortrait } from '../lib/playerHelpers.js';
import { fetchDump, parseSkills, parseLines, parseNumber, isHiddenSkill, skillModifier, formatModifier } from '../lib/fftbgLive.js';
import { activeModifiers, classBonus, STANDARD_CLASSES } from '../lib/skillMods.js';
import { cmdToken, bonusClassTokens, pickClass, buildEntry, buildMonsterEntry, pruneSameSlotBans, forcedSlots, pickRuleSkill, RULE_POOLS, fitToLimit, questTargets, pickQuestTarget, rankMonsters } from '../lib/entryAdvisor.js';
import { loadPrefs } from '../lib/userPrefs.js';
import { loadData } from '../lib/dataStore.js';
import { SEASON_COUNT, seasonOf } from '../lib/seasons.js';
import { vipStandings, vipRowFor, vipMissing, VIP_MONSTERS, VIP_CLASSES } from '../lib/vipQuest.js';
import { vipCandidates } from '../lib/vipAdvisor.js';
import { classKit, ownSkillset, skillsetKit, defensesOf } from '../lib/vipAdvisorLive.js';
import genTokens from '../data/genTokens.json';
import { classIndex, rosterStats } from '../lib/preload.js';
import { genderEdge, avoidPicks } from '../lib/classAdviceLive.js';

const nameOf = (tok) => (genTokens.byToken[tok] && genTokens.byToken[tok].name) || tok.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
const isMonster = (tok) => genTokens.byToken[tok] && genTokens.byToken[tok].category === 'monster';
const rarityOf = (tok) => (isHiddenSkill(tok) ? 'rare' : (genTokens.byToken[tok] && genTokens.byToken[tok].rarity) || 'common');
const classDisplay = (tok) => String(tok).replace(/([a-z0-9])([A-Z])/g, '$1 $2');
// token → combat win rate (human classes only), for ranking entry classes by power.
// Built on first use: classIndex arrives from S3 after this module loads (lib/preload.js).
let CLASS_WR = null;
const classWR = (tok) => {
  if (!CLASS_WR) { CLASS_WR = {}; for (const r of classIndex) if (!r.monster) CLASS_WR[cmdToken(r.cls)] = r.winRate; }
  return CLASS_WR[cmdToken(tok)];
};
const FORCEABLE = ['skillset', 'support', 'reaction', 'movement', 'accessory'];
const RARITY_RANK = { rare: 2, uncommon: 1, common: 0 };
// genTokens category → the crossByClass dim that holds its per-class win rates.
const CAT_DIM = { skillset: 'ActionSkill', support: 'SupportSkill', reaction: 'ReactionSkill', movement: 'MoveSkill', accessory: 'Accessory' };
// The inverse: a worstBan crossByClass dim → its genTokens category (for slot dedup).
const DIM_CAT = { ActionSkill: 'skillset', SupportSkill: 'support', ReactionSkill: 'reaction', MoveSkill: 'movement', Accessory: 'accessory' };
// Skill display-name → genTokens category, so a ban (which carries only a name)
// can be matched against the forced slot. Names are unique across categories.
const NAME_CAT = {};
for (const k in genTokens.byToken) NAME_CAT[genTokens.byToken[k].name] = genTokens.byToken[k].category;
const tokenCategory = (tok) => (genTokens.byToken[tok] && genTokens.byToken[tok].category) || '';
const GGH_PREF = 'wiznaibus.mods.excludeGgh';
// "Save my hidden skills": no suggestion forces a hidden (prestige) skill. For the hoarders.
const HIDDEN_PREF = 'wiznaibus.mods.saveHidden';
// "A, B or C", for the names in a sentence.
const orList = (xs) => (xs.length < 2 ? xs.join('') : xs.slice(0, -1).join(', ') + ' or ' + xs[xs.length - 1]);
// A class's base win rate, for one gender when given, so a gendered skill rate is
// compared like for like (female Monks win 48.0% overall against 51.8% for both).
const classBaseWR = (cls, gender) => {
  const g = gender && (rosterStats.windows.all.ClassGender || []).find((x) => x.value === cls + ' ' + gender);
  return g ? g.winRate : ((rosterStats.windows.all.Class.find((x) => x.value === cls)) || {}).winRate || 50;
};
// A skill's empirical win rate FOR a class, or null on thin/absent data. This is
// the "same checker" — so e.g. Short Charge reads +6.8% on Wizard but ~0% on melee,
// instead of being force-picked by rarity. Pass a gender ('Male'/'Female') to score
// for that gender only; omit it to combine both (the Go-big default).
function skillClassWR(token, cls, gender) {
  const meta = genTokens.byToken[token];
  const dim = meta && CAT_DIM[meta.category];
  const rows = dim && (rosterStats.crossByClass[dim] || {})[meta.name];
  if (!rows) return null;
  const rel = gender
    ? rows.filter((r) => r.value === cls + ' ' + gender)
    : rows.filter((r) => r.value === cls + ' Male' || r.value === cls + ' Female');
  const tot = rel.reduce((s, r) => s + r.played, 0);
  const floor = gender ? 150 : 300;  // a single-gender slice is half the sample
  return tot >= floor ? rel.reduce((s, r) => s + r.winRate * r.played, 0) / tot : null;
}
// Gender-locked classes — the off-gender pill is disabled (matches the generator).
const GENDER_LOCK = { Bard: 'Male', Dancer: 'Female' };
// owned monster → !fight tier (rare species = elite, uncommon = strong, else normal)
const tierOf = (tok) => { const r = rarityOf(tok); return r === 'rare' ? 'elite' : r === 'uncommon' ? 'strong' : 'normal'; };
// The same, '' for anything that isn't a monster (RandomMonster is a meta token, not a species).
const monsterTier = (tok) => (isMonster(tok) ? tierOf(tok) : '');
const TIER_PRIO = { elite: '−15 priority', strong: '−10 priority', normal: 'no priority cost' };
// 🐉 "Respect Monster Quest": only suggest monsters you still need for the next set.
const MQ_PREF = 'wiznaibus.mods.monsterQuest';

// ── ⭐ My entry (Taobakaa's suggestion): a remembered class + rules → one command ──
const CLASS_PREF = 'wiznaibus.mods.class';   // the remembered "Suggest for" class
// The remembered gender (null = the class's stronger one, '' = any), set by the picker's ♂/♀
// or My entry's pills. It's kept in the My entry rules too; this copy works without them.
const GENDER_PREF = 'wiznaibus.mods.gender';
const AUTO_VAL = '__auto__';   // the picker's ⚙ Auto tile ('' can't be a tile value)
const portraitOf = (cls, gender) => UNITS_BASE + classPortrait(cls, gender);
const ENTRY_PREF = 'wiznaibus.mods.entry';   // the My entry rules; present = set up
// gender: null = the class's stronger gender (if there is one), '' = any, or a gender.
const ENTRY_DEFAULTS = { forceBest: true, pool: 'usable', banWorst: false, respectQuest: false, vip: false, gender: null };
// 🎖️ VIP Quest helper. Strong and elite monsters share a cooldown, so the ones you still
// need go first (the VIP_MONSTERS tiers, from the token rarity a !fight monster is priced by).
const VIP_MONSTER_SET = new Set(VIP_MONSTERS);
const MONSTER_TIER = {};
for (const k in genTokens.byToken) {
  const t = genTokens.byToken[k];
  if (t.category === 'monster' && (t.rarity === 'rare' || t.rarity === 'uncommon')) MONSTER_TIER[t.name] = t.rarity === 'rare' ? 'elite' : 'strong';
}
const VIP_SHOWN = 5;   // candidates listed under the pick
const POOL_LABEL = { usable: 'Any usable', offcd: 'Off cooldown', nopenalty: 'No penalty' };
// Quests an entry can move: their targets are classes (gendered or not) and monsters.
const QUEST_ENTERABLE = new Set(['monster', 'human', 'everything', 'gendered', 'bagel']);
const QUEST_WHY_NOT = { zodiac: "a sign can't be forced", carried: "it counts teammates' monsters, not yours" };
const HUMAN_BY_NAME = Object.fromEntries(STANDARD_CLASSES.map((t) => [classDisplay(t).toLowerCase(), t]));
const MONSTER_BY_NAME = {};
for (const k in genTokens.byToken) if (genTokens.byToken[k].category === 'monster') MONSTER_BY_NAME[genTokens.byToken[k].name.toLowerCase()] = k;
// A monster's combat win rate by name, to rank quest monsters (built on first use,
// like CLASS_WR: classIndex arrives from S3 after this module loads).
let MON_WR = null;
const monsterWR = (name) => {
  if (!MON_WR) { MON_WR = {}; for (const r of classIndex) if (r.monster) MON_WR[r.cls.toLowerCase()] = r.winRate; }
  return MON_WR[String(name).toLowerCase()];
};
const readJson = (k) => { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } };
const writeJson = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* private mode — session-only */ } };

export default {
  name: 'MyModifiersModal',
  components: { ClassPickerModal },
  mixins: [modalA11y],
  props: {
    handle: { type: String, required: true },
    // The champion an entry made now would face, from the bracket page:
    // { units, label, decided, isNew } (see Luxstadio's nextChampion).
    champion: { type: Object, default: null },
  },
  emits: ['close'],
  data() {
    return {
      skills: null, skillBonus: '', mark: 0, classbonus: null, gil: 0, copied: '', loaded: false, chosenClass: '',
      entriesExpanded: false, excludeGgh: true, saveHidden: false,
      // ⭐ My entry: the rules (remembered), whether it's been set up, the "adjust picks"
      // panel, and this session's manual picks (null = follow the rule).
      rules: { ...ENTRY_DEFAULTS }, entrySetUp: false, fineTune: false, customGender: '', forceOverride: null, banOverride: null,
      goal: null, goalInfo: null,  // the pinned quest (prefs), for "respect my quest"
      // 🎖️ VIP Quest: the champion history (frozen, loaded when the switch is on), this
      // session's pick of another candidate, and the on-demand sim's results by class.
      vipHist: null, vipMeta: null, vipError: '', vipOverride: null, vipSim: {}, vipSimBusy: false,
      monsterQuest: false,   // 🐉 Monster: only monsters your Monster Quest still needs
      classPickerOpen: false, AUTO_VAL,
      // The 🐉 tile's portrait, just for fun: one of your monsters at random, once they load.
      monsterFace: VIP_MONSTERS[Math.floor(Math.random() * VIP_MONSTERS.length)],
      seasonCount: SEASON_COUNT, vipTotal: VIP_CLASSES.length,
    };
  },
  async created() {
    // Restore the GilgameHeart-exclude preference (default on — the historical behavior).
    try { const v = localStorage.getItem(GGH_PREF); if (v != null) this.excludeGgh = v === '1'; } catch { /* private mode — keep the default */ }
    try { this.saveHidden = localStorage.getItem(HIDDEN_PREF) === '1'; } catch { /* private mode — off */ }
    // The remembered class and My entry rules.
    const cls = readJson(CLASS_PREF);
    if (cls === '' || cls === 'MONSTER' || STANDARD_CLASSES.includes(cls)) this.chosenClass = cls;
    const rules = readJson(ENTRY_PREF);
    if (rules && typeof rules === 'object') { this.rules = { ...ENTRY_DEFAULTS, ...rules }; this.entrySetUp = true; }
    try { const g = localStorage.getItem(GENDER_PREF); if (g != null) { const v = JSON.parse(g); if (v === null || v === '' || v === 'Male' || v === 'Female') this.rules.gender = v; } } catch { /* keep the rules' */ }
    try { this.monsterQuest = localStorage.getItem(MQ_PREF) === '1'; } catch { /* private mode — off */ }
    if (this.rules.vip || (this.monsterQuest && this.chosenClass === 'MONSTER')) this.ensureVipHistory();
    // The pinned goal, which the bracket page keeps current (lib/goalRefresh.js).
    const prefs = loadPrefs();
    this.goal = prefs.activeGoal || null;
    this.goalInfo = prefs.goalInfo || null;
    this.resetCustom();
    // userskills is likely already cached (the drop chip fetched it); the rest are
    // tiny. All prod-only (the dump CORS), so they resolve to null off-prod.
    const [sk, bonus, mk, cb, gil] = await Promise.all([
      fetchDump('userskills', this.handle),
      fetchDump('skillbonus', this.handle),
      fetchDump('prestige/mark', this.handle),
      fetchDump('classbonus', this.handle),
      fetchDump('gil', this.handle),
    ]);
    this.skills = sk == null ? [] : parseSkills(sk);
    this.skillBonus = parseLines(bonus)[0] || '';
    this.mark = parseNumber(mk) || 0;
    this.classbonus = cb == null ? null : parseLines(cb);  // null = fetch failed (off-prod)
    this.gil = parseNumber(gil) || 0;
    const mons = this.skills.filter((s) => isMonster(s.token));
    if (mons.length) this.monsterFace = nameOf(mons[Math.floor(Math.random() * mons.length)].token);
    this.loaded = true;
  },
  computed: {
    mods() {
      return activeModifiers(this.skills, this.skillBonus, this.mark, nameOf, monsterTier);
    },
    cb() {
      return this.classbonus == null ? null : classBonus(this.classbonus, this.skillBonus, isMonster);
    },
    skillBonusName() {
      return this.skillBonus ? nameOf(this.skillBonus) : '';
    },
    hasData() {
      return this.mods.length > 0 || this.cb != null;
    },
    // Best class to enter as: the strongest (by combat win rate) of your bonus
    // classes, falling back to the strongest class overall.
    bestClassToken() {
      const rank = (tok) => classWR(tok) ?? 0;
      const bonus = bonusClassTokens(this.classbonus || []);
      return pickClass(bonus.length ? bonus : STANDARD_CLASSES, rank);
    },
    // The class the suggestions use — your quest's target when "respect my quest" is
    // on, the VIP pick when the VIP helper is, else the picker's (remembered) choice,
    // else the auto-best.
    activeClass() {
      if (this.questActive && this.questTarget.kind === 'class') return this.questTarget.cls;
      if (this.vipActive && this.vipPick.kind === 'class') return HUMAN_BY_NAME[this.vipPick.cls.toLowerCase()] || this.vipPick.cls;
      return this.chosenClass && this.chosenClass !== 'MONSTER' ? this.chosenClass : this.bestClassToken;
    },
    // The stronger gender for the active class, if there's a significant gap (≥2%).
    // null for monsters / gender-locked / negligible. Forcing it costs 100G, no
    // priority hit — a pure win, so we fold it into the value/go-big commands.
    genderEdgeInfo() {
      if (this.chosenClass === 'MONSTER' || !this.activeClass) return null;
      return genderEdge(rosterStats, classDisplay(this.activeClass), { minDelta: 2 });
    },
    // The class's worst forceable skill to ban (e.g. Sing on a Wizard, -8.4%) —
    // surfaced as an OPTIONAL swap for the -gilgameheart ban. We keep GilgameHeart
    // as the default (its +5 priority usually beats the small avoid-the-worst-roll
    // gain); this just shows the alternative. Only returns clearly-bad ones (≤-3%).
    worstBan() {
      if (this.chosenClass === 'MONSTER' || !this.activeClass) return null;
      const cls = classDisplay(this.activeClass);
      const cbc = rosterStats.crossByClass || {};
      const base = ((rosterStats.windows.all.Class.find((x) => x.value === cls)) || {}).winRate || 50;
      const DIMS = { ActionSkill: 'secondary', SupportSkill: 'support', ReactionSkill: 'reaction', MoveSkill: 'move', Accessory: 'accessory' };
      // Don't recommend banning a slot the suggestions already FORCE (skill bonus /
      // best owned skill) — banning a sibling there is wasted gil (same dedup as the
      // custom builder). Skip those slots' dims entirely.
      const forcedCats = new Set();
      for (const tok of [this.skillBonus, this.goBigSkill]) for (const c of forcedSlots(tok, tokenCategory(tok))) forcedCats.add(c);
      let worst = null;
      for (const dim in DIMS) {
        if (forcedCats.has(DIM_CAT[dim])) continue;
        for (const skill in (cbc[dim] || {})) {
          if (/^Equip /.test(skill)) continue;  // off-class weapon noise
          // both genders — the well-known "this class + this skill is bad" headline
          const rows = cbc[dim][skill].filter((r) => r.value === cls + ' Male' || r.value === cls + ' Female');
          const tot = rows.reduce((s, r) => s + r.played, 0);
          if (tot < 500) continue;
          const wr = rows.reduce((s, r) => s + r.winRate * r.played, 0) / tot;
          if (!worst || wr < worst.wr) worst = { skill, slot: DIMS[dim], wr };
        }
      }
      if (!worst) return null;
      const delta = +(worst.wr - base).toFixed(1);
      return delta <= -3 ? { skill: worst.skill, slot: worst.slot, delta, ban: cmdToken(worst.skill) } : null;
    },
    // Class tokens (lowercased) that earn the class bonus — to mark them in the picker.
    bonusSet() {
      return new Set(bonusClassTokens(this.classbonus || []).map(cmdToken));
    },
    // The gender you picked (♂/♀ in the picker or My entry's pills): 'Male' / 'Female',
    // '' = any, null = the class's stronger gender. A gender-locked class ignores it.
    pickedGender() {
      return this.rules.gender;
    },
    // Your skill bonus, when it's a monster you own: entering as it earns the bonus.
    monsterBonus() {
      const sb = this.skillBonus;
      return !!sb && isMonster(sb) && (this.skills || []).some((s) => cmdToken(s.token) === cmdToken(sb));
    },
    // The picker's tiles: ⚙ Auto, the 20 classes (✦ = earns your class bonus; ♂/♀ pick a
    // gender right there), then 🐉 Monster.
    pickerTiles() {
      const g = (tok) => (classDisplay(tok) === classDisplay(this.activeClass) && this.customGender === 'Female' ? 'F' : 'M');
      const best = classDisplay(this.bestClassToken);
      return [
        { val: AUTO_VAL, name: 'Auto: ' + best, emoji: '⚙', special: true, title: `Your strongest ${this.bonusSet.size ? 'bonus ' : ''}class, ${best}` },
        ...STANDARD_CLASSES.map((tok) => {
          const name = classDisplay(tok), bonus = this.bonusSet.has(cmdToken(tok));
          return {
            val: tok, name, icon: portraitOf(name, g(tok)),
            badge: bonus ? '✦' : '', badgeTitle: 'Earns your class bonus',
            genders: GENDER_LOCK[name] ? [] : ['Male', 'Female'],
            title: name + (bonus ? ' ✦ earns your class bonus' : '') + (GENDER_LOCK[name] ? '' : ' · click for the stronger gender, or pick ♂/♀'),
          };
        }),
        {
          val: 'MONSTER', name: 'Monster', icon: portraitOf(this.monsterFace, 'Monster'), special: true,
          badge: this.monsterBonus ? '✦' : '', badgeTitle: 'Your skill bonus is a monster you own',
          title: 'The best monster you own that you can roll right now',
        },
      ];
    },
    // What the "Suggest for" button shows.
    pickerCurrent() {
      if (this.chosenClass === 'MONSTER') return { icon: portraitOf(this.monsterFace, 'Monster'), label: 'Monster', bonus: this.monsterBonus };
      if (!this.chosenClass) return { emoji: '⚙', label: 'Auto: ' + classDisplay(this.bestClassToken), bonus: this.bonusSet.has(cmdToken(this.bestClassToken)) };
      const name = classDisplay(this.chosenClass);
      const g = this.genderLock ? '' : this.customGender === 'Female' ? ' ♀' : this.customGender === 'Male' ? ' ♂' : '';
      return { icon: portraitOf(name, this.customGender === 'Female' ? 'F' : 'M'), label: name + g, bonus: this.bonusSet.has(cmdToken(this.chosenClass)) };
    },
    // Your single best owned forceable skill FOR THE ACTIVE CLASS, by empirical win
    // rate (must beat the class baseline to be worth forcing). Picking by class fit
    // instead of rarity stops it always suggesting Short Charge on melee, etc.
    goBigSkill() {
      const cls = classDisplay(this.activeClass);
      const bonusKey = String(this.skillBonus || '').toLowerCase();
      let best = '', bestWR = classBaseWR(cls);
      for (const s of this.skills || []) {
        const meta = genTokens.byToken[s.token];
        if (!meta || !FORCEABLE.includes(meta.category)) continue;
        // Skip "cannot use" (buyrandom step≥5) skills — they can't be forced.
        const mod = skillModifier(s.step, s.token.toLowerCase() === bonusKey);
        if (mod && mod.cannotUse) continue;
        if (this.saveHidden && isHiddenSkill(s.token)) continue;
        const wr = skillClassWR(s.token, cls);
        if (wr != null && wr > bestWR) { bestWR = wr; best = s.token; }
      }
      return best;
    },
    // The suggested !fight entries (only with the prod-only data loaded). Always a
    // "Get me in" baseline, then class-specific rows (or owned-monster rows).
    entries() {
      if (this.classbonus == null) return null;
      const aff = (cost) => cost <= this.gil;
      const getIn = { key: 'in', icon: '🎯', label: 'Get me in', note: 'Best odds — a random unit, but it still banks your class-bonus EXP.', ...buildEntry({}), affordable: true };

      if (this.chosenClass === 'MONSTER') return [...this.monsterEntries, getIn];

      const classTok = this.activeClass;
      const classDisp = classDisplay(classTok);
      const sb = this.skillBonus;
      const sbMonster = !!sb && (genTokens.byToken[sb] || {}).category === 'monster';
      const sbSaved = !!sb && this.saveHidden && isHiddenSkill(sb);
      const forceSb = !!sb && !sbMonster && !sbSaved;   // a monster skill bonus can't be forced on a human
      const gx = this.genderEdgeInfo;          // { gender, delta } | null
      // Your picked gender ('' = any), else the stronger one when the gap is real.
      const gender = this.genderLock ? '' : this.pickedGender != null ? this.pickedGender : gx ? gx.gender : '';

      // Excluding GilgameHeart buys +5 priority but costs +500G (the bribe). Fold it
      // in only when the forced skill isn't GilgameHeart AND you can afford it —
      // otherwise drop it (poor players shouldn't be told to pay a bribe they can't).
      const buildAdaptive = (forcedSkills, usesSkillBonus) => {
        const b = { className: classTok, gender, forcedSkills, usesSkillBonus, rarityOf };
        // Opt-out (persisted) or already forcing GilgameHeart → no bribe.
        if (!this.excludeGgh || forcedSkills.some((s) => cmdToken(s) === 'gilgameheart')) return { e: buildEntry(b), bribe: false, poor: false };
        const withBribe = buildEntry({ ...b, excludes: ['GilgameHeart'] });
        if (withBribe.cost <= this.gil) return { e: withBribe, bribe: true, poor: false };
        return { e: buildEntry(b), bribe: false, poor: true };
      };

      const v = buildAdaptive(forceSb ? [sb] : [], forceSb);
      const rows = [getIn, {
        key: 'value', icon: '⚖️', label: 'Smart value',
        note: `Force ${classDisp}${forceSb ? ' + your skill bonus (' + this.skillBonusName + ')' : ''}`
          + (v.bribe ? ', exclude GilgameHeart — same odds as a blind entry, but you control it.'
            : v.poor ? ' — you control it (skipped the GilgameHeart bribe; you\'re short on gil for the +5).' : ' — you control it.')
          + (sbMonster ? ` Your skill bonus, ${this.skillBonusName}, is a monster — enter as it via 🐉 Monster to claim its +5.` : '')
          + (sbSaved ? ` Your skill bonus, ${this.skillBonusName}, is a hidden skill you're saving, so it's left out.` : ''),
        ...v.e, affordable: aff(v.e.cost),
      }];

      const big = this.goBigSkill;
      if (big && cmdToken(big) !== cmdToken(sb)) {
        const b = buildAdaptive([big], false);
        const r = rarityOf(big);
        const tail = r === 'common' ? 'no odds cost.' : `but it's ${r}, so it drops your odds.`;
        rows.push({
          key: 'big', icon: '💪', label: 'Go big',
          note: `Force ${classDisp} + ${nameOf(big)} — your best owned skill for ${classDisp} — ${tail}` + (b.poor ? ' (GilgameHeart bribe skipped — low on gil.)' : ''),
          ...b.e, affordable: aff(b.e.cost),
        });
      }
      return rows;
    },
    // The one entry shown when the section is collapsed — the recommended Smart
    // value (controlled + good odds), falling back to the first available.
    headlineEntry() {
      const e = this.entries;
      return e ? (e.find((x) => x.key === 'value' || x.mon) || e[0]) : null;
    },
    // Your owned monsters as solo entries, ranked: what you can enter as right now (off
    // cooldown, affordable) by win rate, then the rest. Strong and elite monsters share a
    // cooldown per tier, read off the userskills tildes (entryAdvisor rankMonsters).
    rankedMonsters() {
      return rankMonsters(this.skills, {
        tierOf: monsterTier, winRate: (t) => monsterWR(nameOf(t)), modifier: skillModifier, format: formatModifier,
        gil: this.gil, skillBonus: this.skillBonus,
      });
    },
    // 🐉 Monster Quest progress, all time: how many of the 55 you've champed, how many full
    // sets, and the monsters the next set still needs (all at your lowest count). null until
    // the champion history loads.
    mqProgress() {
      if (!this.vipHist) return null;
      const row = vipRowFor(vipStandings(this.vipHist, 0, seasonOf, this.vipMeta), this.handle);
      const count = (m) => (row && row.cls[m] ? row.cls[m].titles : 0);
      const sets = Math.min(...VIP_MONSTERS.map(count));
      return { sets, have: VIP_MONSTERS.filter((m) => count(m) > sets).length, need: new Set(VIP_MONSTERS.filter((m) => count(m) === sets)) };
    },
    // The monsters you own and can enter as right now, by name (cooldown-aware): what the
    // VIP helper and "Respect my quest" may suggest. Affordability is left to the entry.
    rollableMonsters() {
      return new Set(this.rankedMonsters.filter((r) => r.usable).map((r) => nameOf(r.token)));
    },
    // The monsters the suggestions draw from: all you own, or just what your quest needs.
    monsterPool() {
      const mq = this.monsterQuest && this.mqProgress;
      return mq ? this.rankedMonsters.filter((r) => mq.need.has(nameOf(r.token))) : this.rankedMonsters;
    },
    // The ones you can enter as, as entry rows (an unaffordable one stays, marked).
    monsterEntries() {
      const rows = this.monsterPool.filter((r) => r.usable);
      return rows.map((r, i) => {
        const name = nameOf(r.token);
        const wr = r.winRate != null ? r.winRate.toFixed(1) + '% win rate' : 'no win-rate data';
        const best = i === 0 && r.affordable && r.winRate != null ? (this.monsterQuest ? ', your best that the quest still needs' : ', the best you can roll right now') : '';
        const tierBit = `${r.tier} monster (${TIER_PRIO[r.tier]})`;
        const cd = r.modText && !r.isBonus ? (r.tier === 'normal' ? ` · rerolled: ${r.modText}` : ` · ${r.tier} cooldown winding down: ${r.modText}`) : '';
        return {
          key: 'mon-' + r.token, mon: true, icon: '🐉', label: name + (r.winRate != null ? ' · ' + r.winRate.toFixed(1) + '%' : '') + (r.isBonus ? ' ✦' : ''),
          note: wr + best + ' · ' + tierBit + cd + (r.isBonus ? ' · your skill bonus (+5 priority, +2 EXP)' : '') + '. A monster entry takes no other parameters.',
          ...r,
        };
      });
    },
    // Owned monsters you can't enter as right now, in a line per cooldown: strong and
    // elite ones by tier (they share it), normal ones together (each has its own).
    monsterBlocked() {
      const out = [], blocked = this.monsterPool.filter((r) => !r.usable);
      for (const tier of ['elite', 'strong', 'normal']) {
        const rows = blocked.filter((r) => r.tier === tier);
        if (!rows.length) continue;
        const names = rows.map((r) => nameOf(r.token));
        const list = names.slice(0, 5).join(', ') + (names.length > 5 ? ` and ${names.length - 5} more` : '');
        if (tier === 'normal') {
          out.push({ key: tier, text: `On their own cooldown: ${list}.` });
        } else {
          const n = rows[0].freesIn;
          out.push({ key: tier, text: `Your ${tier} monsters share a cooldown, and it's still running: about ${n} more tournament${n === 1 ? '' : 's'} you're in before any of them can roll (${list}).` });
        }
      }
      return out;
    },
    mqHint() {
      const mq = this.mqProgress;
      if (this.vipError) return "Couldn't load your titles, so this shows every monster you own.";
      if (!mq) return 'Reading your titles…';
      const n = VIP_MONSTERS.length;
      return mq.sets ? `${mq.sets} full set${mq.sets === 1 ? '' : 's'} · ${mq.have}/${n} toward the next` : `${mq.have}/${n} champed`;
    },
    // Why the quest switch left nothing to suggest, if it did.
    mqEmptyMsg() {
      if (!this.monsterQuest || !this.mqProgress || this.monsterEntries.length) return '';
      if (!this.monsterPool.length) return "You don't own any of the monsters your Monster Quest still needs.";
      return 'The monsters you own that your quest still needs are all on cooldown right now.';
    },
    // ── ⭐ My entry ───────────────────────────────────────────────────────────
    showMyEntry() {
      return this.entrySetUp && this.chosenClass !== 'MONSTER';
    },
    // The pinned quest, if an entry can advance it.
    questGoal() {
      const g = this.goal, info = this.goalInfo;
      if (!g || g.type !== 'quest' || !info || !QUEST_ENTERABLE.has(g.id)) return null;
      return { id: g.id, name: info.name, remaining: info.remaining || [] };
    },
    // Your quest's best target you can enter as (a class, with gender on gendered
    // quests, or a monster you own), or null.
    questTarget() {
      const q = this.questGoal;
      if (!q) return null;
      const owned = new Set((this.skills || []).filter((s) => isMonster(s.token)).map((s) => s.token));
      const targets = questTargets(q.remaining, {
        humanClass: (n) => HUMAN_BY_NAME[n.toLowerCase()] || '',
        monsterToken: (n) => { const t = MONSTER_BY_NAME[n.toLowerCase()]; return t && owned.has(t) && this.rollableMonsters.has(nameOf(t)) ? t : ''; },
      });
      return pickQuestTarget(targets, {
        isBonus: (cls) => this.bonusSet.has(cmdToken(cls)),
        rank: (t) => (t.kind === 'class' ? classWR(t.cls) : monsterWR(t.label)) ?? 0,
      });
    },
    questActive() {
      return this.rules.respectQuest && !!this.questTarget;
    },
    // Why "respect my quest" can't apply ('' when it can), for its tooltip and note.
    questWhyNot() {
      const g = this.goal;
      if (!g) return 'Pin a quest on your profile first.';
      if (g.type !== 'quest') return "Your goal is an achievement, which an entry can't target.";
      if (QUEST_WHY_NOT[g.id]) return `Your quest can't be targeted: ${QUEST_WHY_NOT[g.id]}.`;
      if (!this.questGoal) return "Your goal can't be targeted by an entry.";
      if (!this.questTarget) return `Nothing left on your ${this.questGoal.name} list is something you can enter as.`;
      return '';
    },
    // The locked gender for the active class (Bard/Dancer), or '' if free.
    genderLock() {
      return GENDER_LOCK[classDisplay(this.activeClass)] || '';
    },
    // The gender a quest target needs ('' if none, or when the class is locked anyway).
    questGender() {
      return this.questActive && this.questTarget.kind === 'class' && !this.genderLock ? this.questTarget.gender : '';
    },
    // The gender the entry names ('' = leave it to the roll). A locked class never needs one.
    genderParam() {
      return this.genderLock ? '' : this.customGender;
    },
    // The slots the forced skill fills (its own, plus the secondary for Throw Item /
    // Monster Talk) — drives the same-slot ban dedup.
    forceSlots() {
      return forcedSlots(this.forcePick, tokenCategory(this.forcePick));
    },
    // Your hidden (prestige) skills that an entry could force, by name. "Save my hidden
    // skills" only shows when there's one to save.
    ownedHidden() {
      return (this.skills || []).filter((s) => isHiddenSkill(s.token) && FORCEABLE.includes(tokenCategory(s.token))).map((s) => nameOf(s.token));
    },
    // Your owned forceable skills, ranked by empirical win rate for the active class
    // (and gender, when one is set). "Cannot use" (5+ tildes) skills are left out.
    // Hidden skills are in it; forceRanked drops them while you're saving them.
    forceRankedAll() {
      const cls = classDisplay(this.activeClass);
      const base = classBaseWR(cls, this.customGender);
      const bonusKey = String(this.skillBonus || '').toLowerCase();
      const out = [];
      for (const s of this.skills || []) {
        const meta = genTokens.byToken[s.token];
        if (!meta || !FORCEABLE.includes(meta.category)) continue;
        const isBonus = s.token.toLowerCase() === bonusKey;
        const mod = skillModifier(s.step, isBonus);
        if (mod && mod.cannotUse) continue;
        const wr = skillClassWR(s.token, cls, this.customGender);
        if (wr == null) continue;
        const r = rarityOf(s.token);
        out.push({
          token: s.token, name: nameOf(s.token), step: s.step, isBonus,
          delta: +(wr - base).toFixed(1), rarity: r,
          prio: r === 'rare' ? -15 : r === 'uncommon' ? -10 : 0,
          // The owned-skill step modifier (rerolled penalty, or the +2/+5 bonus).
          stepText: mod && !mod.cannotUse ? formatModifier(mod) : '',
          stepNeg: !!mod && !mod.cannotUse && mod.exp < 0,
        });
      }
      return out.sort((a, b) => b.delta - a.delta);
    },
    // The ranking every pick uses: My entry's rule, the Force list, the notes.
    forceRanked() {
      return this.saveHidden ? this.forceRankedAll.filter((s) => !isHiddenSkill(s.token)) : this.forceRankedAll;
    },
    // The Force list: the top 10, plus the current pick if it ranks lower.
    customForceList() {
      const top = this.forceRanked.slice(0, 10);
      const pick = this.forcePick && this.forceRanked.find((s) => s.token === this.forcePick);
      return pick && !top.includes(pick) ? [...top, pick] : top;
    },
    // The Ban list: the class's worst rolls (for the gender, when one is set), without
    // the slot you're already forcing (a same-slot ban just burns 100G). GilgameHeart
    // has its own checkbox.
    customBanList() {
      const cls = classDisplay(this.activeClass);
      const base = classBaseWR(cls, this.customGender);
      const worst = avoidPicks(rosterStats, cls, { gender: this.customGender, n: 8 })
        .map((w) => ({ token: w.skill, name: w.skill, delta: +(w.winRate - base).toFixed(1) }));
      return pruneSameSlotBans(worst, this.forceSlots, (b) => NAME_CAT[b.name] || '');
    },
    // What the rules pick, and what's in effect (a manual pick this session wins). The VIP
    // helper's matchup skill, when it found one, comes before the best-skill rule.
    ruleForce() {
      if (this.vipSkill) return this.vipSkill.token;
      return this.rules.forceBest ? pickRuleSkill(this.forceRanked, this.rules.pool) : '';
    },
    ruleBan() {
      return this.rules.banWorst && this.customBanList.length ? this.customBanList[0].token : '';
    },
    forcePick() {
      return this.forceOverride !== null ? this.forceOverride : this.ruleForce;
    },
    banPick() {
      return this.banOverride !== null ? this.banOverride : this.ruleBan;
    },
    // The assembled entry: fitted to the four-parameter limit, with the GilgameHeart
    // bribe dropped when you can't afford it. A quest target that's a monster you own
    // is a solo monster entry instead.
    customEntry() {
      const t = this.questActive ? this.questTarget
        : this.vipActive && this.vipPick.kind === 'monster' ? { kind: 'monster', token: MONSTER_BY_NAME[this.vipPick.cls.toLowerCase()] } : null;
      if (t && t.kind === 'monster') {
        const tier = tierOf(t.token), e = buildMonsterEntry(t.token, tier);
        if (cmdToken(t.token) === cmdToken(this.skillBonus)) e.priority += 5;
        return { ...e, kind: 'monster', via: this.questActive ? 'quest' : 'vip', monster: t.token, tier, affordable: e.cost <= this.gil, dropped: [], bribeSkipped: false, gender: '', forced: '', ban: '', ggh: false, usesSkillBonus: false, stepText: '' };
      }
      const isGgh = (x) => cmdToken(x) === 'gilgameheart';
      const force = this.forcePick;
      const excludes = [];
      if (this.banPick) excludes.push(this.banPick);
      if (this.excludeGgh && !isGgh(force)) excludes.push('GilgameHeart');
      const fit = fitToLimit({ className: this.activeClass, gender: this.genderParam, genderRequired: !!this.questGender, forcedSkills: force ? [force] : [], excludes });
      const forced = fit.forcedSkills[0] || '';
      const usesSkillBonus = !!forced && !isMonster(forced) && cmdToken(forced) === cmdToken(this.skillBonus);
      const make = (ex) => buildEntry({ className: this.activeClass, gender: fit.gender, forcedSkills: fit.forcedSkills, excludes: ex, usesSkillBonus, rarityOf });
      let ex = fit.excludes, e = make(ex), bribeSkipped = false;
      if (ex.some(isGgh) && e.cost > this.gil) { ex = ex.filter((x) => !isGgh(x)); e = make(ex); bribeSkipped = true; }
      // buildEntry is blind to buyrandom-step penalties — fold in the forced skill's
      // own step modifier. (The skill-bonus +5 is already in e.priority via
      // usesSkillBonus, so only adjust for a non-bonus rerolled skill.)
      let stepText = '';
      let priority = e.priority;
      if (forced && !usesSkillBonus) {
        const s = (this.skills || []).find((x) => x.token === forced);
        const m = s ? skillModifier(s.step, false) : null;
        if (m && !m.cannotUse && m.priority) { priority += m.priority; stepText = formatModifier(m); }
      }
      return { ...e, kind: 'class', priority, usesSkillBonus, stepText, affordable: e.cost <= this.gil, dropped: fit.dropped, bribeSkipped, gender: fit.gender, forced, ban: ex.find((x) => !isGgh(x)) || '', ggh: ex.some(isGgh) };
    },
    // What the any usable / off cooldown / no penalty choice changed here, so it never
    // looks inert: the better skills it passed over (and their costs), then what each
    // stricter choice would force instead.
    poolNote() {
      if (!this.rules.forceBest || this.forceOverride !== null || this.customEntry.kind === 'monster' || this.vipSkill) return '';
      const cur = this.ruleForce, at = Math.max(0, RULE_POOLS.indexOf(this.rules.pool));
      const picks = RULE_POOLS.map((p) => pickRuleSkill(this.forceRanked, p));
      if (picks.every((t) => t === cur)) return cur ? `All three choices pick ${nameOf(cur)} here.` : '';
      const info = (tok) => this.forceRanked.find((x) => x.token === tok);
      // Only the costs this choice turns down: tildes, plus rarity under "no penalty".
      const costs = (o) => [
        o.step > 0 && !o.isBonus ? `${o.step} reroll tilde${o.step === 1 ? '' : 's'}` : '',
        this.rules.pool === 'nopenalty' && o.rarity && o.rarity !== 'common' ? `${o.rarity}, ${o.prio} priority` : '',
      ].filter(Boolean).join('; ');
      const out = [];
      const skipped = [...new Set(picks.slice(0, at))].filter((t) => t && t !== cur).map(info).filter(Boolean);
      if (skipped.length) out.push('Skipped ' + skipped.map((o) => `${o.name} (+${o.delta}%: ${costs(o)})`).join(' and ') + '.');
      for (let i = at + 1; i < RULE_POOLS.length; i++) {
        const t = picks[i];
        if (t === cur || t === picks[i - 1] && i > at + 1) continue;   // same as this choice, or as the one just listed
        const o = info(t), label = POOL_LABEL[RULE_POOLS[i]], strict = RULE_POOLS[i] === 'nopenalty';
        out.push(o ? `"${label}" would force ${o.name} instead (+${o.delta}%, no tildes${strict ? ' or priority cost' : ''}).`
          : `"${label}" would force nothing: none of your skills that beat the baseline ${strict ? 'are penalty-free' : 'are off cooldown'}.`);
      }
      return out.join(' ');
    },
    // The hidden skill "Save my hidden skills" kept My entry from forcing, if any.
    hiddenNote() {
      if (!this.saveHidden || !this.rules.forceBest || this.forceOverride !== null || this.customEntry.kind === 'monster' || this.vipSkill) return '';
      const tok = pickRuleSkill(this.forceRankedAll, this.rules.pool);
      const o = tok && isHiddenSkill(tok) && this.forceRankedAll.find((s) => s.token === tok);
      return o ? `Skipped ${o.name} (+${o.delta}%: a hidden skill you're saving).` : '';
    },
    // ── 🎖️ VIP Quest ──────────────────────────────────────────────────────────
    // Your row on this season's VIP board: null until the champion history loads, or when
    // you haven't champed anything this season yet.
    vipRow() {
      return this.vipHist ? vipRowFor(vipStandings(this.vipHist, SEASON_COUNT, seasonOf, this.vipMeta), this.handle) : null;
    },
    vipNeeded() { return vipMissing(this.vipRow); },
    vipHave() { return this.vipTotal - this.vipNeeded.length; },
    // The champion's units, read for what they take double from and what they shrug off.
    vipDefenders() { return ((this.champion && this.champion.units) || []).map(defensesOf); },
    // Every class you still need that you can enter as, best first: owned strong and elite
    // monsters ahead of everything (they share a cooldown), then by win rate plus the edge
    // on the champion. A human may bring an owned skillset that hits a weakness.
    vipRanked() {
      if (!this.rules.vip || !this.vipHist) return [];
      const owned = this.rollableMonsters;
      const bonusKey = cmdToken(this.skillBonus);
      const skillsets = [];
      for (const s of this.skills || []) {
        if (tokenCategory(s.token) !== 'skillset' || (this.saveHidden && isHiddenSkill(s.token))) continue;
        const mod = skillModifier(s.step, cmdToken(s.token) === bonusKey);
        if (mod && mod.cannotUse) continue;
        const k = skillsetKit(s.token);
        if (k) skillsets.push({ token: s.token, name: k.name, kit: k.kit, rank: (cls) => skillClassWR(s.token, cls) ?? 0 });
      }
      return vipCandidates({
        needed: this.vipNeeded, owned, skillsets, ownSkillset, kitOf: classKit, defenders: this.vipDefenders,
        isMonster: (c) => VIP_MONSTER_SET.has(c),
        gated: (c) => !!MONSTER_TIER[c],
        strength: (c) => (VIP_MONSTER_SET.has(c) ? monsterWR(c) : classWR(c)),
      });
    },
    // The pick: your choice from the list this session, else the top candidate.
    vipPick() {
      const r = this.vipRanked;
      return (this.vipOverride && r.find((x) => x.cls === this.vipOverride)) || r[0] || null;
    },
    vipActive() { return this.rules.vip && !!this.vipPick; },
    vipSkill() { return this.vipActive && this.vipPick.kind === 'class' ? this.vipPick.skill : null; },
    // The list under the pick: the top few, plus the pick when it ranks lower.
    vipList() {
      const top = this.vipRanked.slice(0, VIP_SHOWN);
      return this.vipPick && !top.includes(this.vipPick) ? [...top, this.vipPick] : top;
    },
    // The needed strong/elite monsters: the ones you own lead the list; the rest you'd
    // have to get first.
    vipGated() {
      const need = this.vipNeeded.filter((c) => MONSTER_TIER[c]);
      const owned = new Set(this.vipRanked.filter((r) => r.gated).map((r) => r.cls));
      const all = new Set(this.rankedMonsters.map((r) => nameOf(r.token)));
      return {
        need, owned: need.filter((c) => owned.has(c)),
        cooling: need.filter((c) => !owned.has(c) && all.has(c)), missing: need.filter((c) => !all.has(c)),
      };
    },
    vipChampText() {
      const c = this.champion;
      if (!c || !c.units || !c.units.length) return '';
      const who = c.units.map((u) => u.Class).join(', ');
      if (c.isNew) return `the new champion, ${c.label}'s team: ${who}`;
      return `the champion${c.decided ? '' : ', if they hold the belt'}: ${who}`;
    },
    // Why the active pick, for the breakdown's VIP line, and why its forced skillset.
    vipPickWhy() { return this.vipPick ? this.vipWhy(this.vipPick) : ''; },
    vipSkillWhy() {
      const hits = this.vipSkill ? this.vipPick.edge.hits.map((h) => `${h.element} for their ${h.cls}`) : [];
      return 'the VIP matchup' + (hits.length ? ': ' + hits.join(', ') : '');
    },
    // The short "for" label on the My entry card: "Lancer ♂", with 🎯 when it's your quest's pick.
    myEntryFor() {
      const e = this.customEntry;
      if (e.kind === 'monster') return nameOf(e.monster) + (e.via === 'vip' ? ' 🎖️' : ' 🎯');
      const g = e.gender === 'Male' ? ' ♂' : e.gender === 'Female' ? ' ♀' : '';
      return classDisplay(this.activeClass) + g + (this.questActive ? ' 🎯' : this.vipActive ? ' 🎖️' : '');
    },
    // What the four-parameter limit left out, in words.
    droppedText() {
      const words = { ban: (v) => 'the ' + v + ' ban', gender: (v) => 'the ' + (v === 'Female' ? '♀' : '♂') + ' pick', bribe: () => 'the GilgameHeart bribe', skill: (v) => nameOf(v) };
      return this.customEntry.dropped.map((d) => words[d.kind](d.value)).join(', ');
    },
    // Display helpers for the entry breakdown.
    classBaseWRDisp() {
      return classBaseWR(classDisplay(this.activeClass), this.customGender).toFixed(1);
    },
    forcePickDisp() {
      const hit = this.forceRanked.find((s) => s.token === this.customEntry.forced);
      if (!hit) return '';
      const sign = hit.delta >= 0 ? '+' : '';
      const tail = hit.rarity === 'common' ? 'common (no priority cost)' : `${hit.rarity} (${hit.prio} priority)`;
      const who = classDisplay(this.activeClass) + (this.customGender === 'Female' ? ' ♀' : this.customGender === 'Male' ? ' ♂' : '');
      return `${sign}${hit.delta}% for ${who} · ${tail}`;
    },
    banPickDisp() {
      const hit = this.customBanList.find((b) => b.token === this.customEntry.ban);
      return hit ? `${hit.delta}%` : '';
    },
  },
  watch: {
    // Switching class re-applies the rules and re-defaults the gender.
    activeClass: 'resetCustom',
    // A new champion makes the VIP sim's numbers stale.
    champion() { this.vipSim = {}; },
    // Flipping gender re-ranks both lists, so fall back to the rules' picks.
    customGender() { this.forceOverride = null; this.banOverride = null; },
    // Remember the picked class. 🐉 Monster has no My entry, so close its picks panel.
    chosenClass(v) {
      writeJson(CLASS_PREF, v);
      if (v === 'MONSTER') { this.fineTune = false; if (this.monsterQuest) this.ensureVipHistory(); }
    },
    rules: { deep: true, handler(v) { if (this.entrySetUp) writeJson(ENTRY_PREF, v); } },
    // Forcing a slot makes a same-slot manual ban pointless, so fall back to the rule.
    forcePick() {
      if (!this.forceSlots.length || !this.banOverride) return;
      const banCat = NAME_CAT[this.banOverride] || tokenCategory(this.banOverride);
      if (this.forceSlots.includes(banCat)) this.banOverride = null;
    },
  },
  methods: {
    classDisplay,  // expose module helpers to the template
    cmdToken,
    // Re-apply the rules (dropping this session's manual picks) and re-default the
    // gender: a locked class's, a quest target's, your remembered pick, else the class's
    // stronger gender when the gap is real, else leave it to the roll.
    resetCustom() {
      this.forceOverride = null;
      this.banOverride = null;
      const g = this.rules.gender;
      const edge = genderEdge(rosterStats, classDisplay(this.activeClass), { minDelta: 2 });
      this.customGender = this.genderLock || this.questGender || (g != null ? g : (edge ? edge.gender : ''));
    },
    selectForce(token) {
      this.forceOverride = this.forcePick === token ? '' : token;
    },
    selectBan(token) {
      this.banOverride = this.banPick === token ? '' : token;
    },
    // A gender picked on the pills is remembered for next time ('' = any).
    setGender(g) {
      this.customGender = g;
      this.rules.gender = g;
      writeJson(GENDER_PREF, g);
    },
    // A picker tile: the class (or Auto / Monster), with a gender when ♂/♀ was clicked.
    // Clicking the tile itself hands the gender back to the class's stronger one.
    onPickClass(val, gender) {
      this.classPickerOpen = false;
      this.chosenClass = val === AUTO_VAL ? '' : val;
      if (val === 'MONSTER' || val === AUTO_VAL) return;
      this.rules.gender = gender || null;
      writeJson(GENDER_PREF, this.rules.gender);
      this.resetCustom();
    },
    setUpMyEntry() {
      this.entrySetUp = true;
      writeJson(ENTRY_PREF, this.rules);
    },
    toggleRule(key) {
      this.rules[key] = !this.rules[key];
      // "Respect my quest" and the VIP helper both choose the class, so one turns the other off.
      if (this.rules[key] && key === 'vip') { this.rules.respectQuest = false; this.ensureVipHistory(); }
      if (this.rules[key] && key === 'respectQuest') this.rules.vip = false;
      if (key === 'vip') this.vipOverride = null;
      this.resetCustom();
    },
    // ── 🎖️ VIP Quest ──
    async ensureVipHistory() {
      if (this.vipHist || this._vipLoading) return;
      this._vipLoading = true;
      try {
        // The reign side-file says when each reign was dethroned, which decides its season.
        const [hist, meta] = await Promise.all([loadData('champHistory.json'), loadData('champReignMeta.json').catch(() => null)]);
        this.vipMeta = meta && Object.freeze(meta);
        this.vipHist = Object.freeze(hist);
      } catch (e) { this.vipError = "Couldn't load the champion history, so the VIP helper can't tell what you still need."; }
      finally { this._vipLoading = false; }
    },
    vipTier(cls) { return MONSTER_TIER[cls] || ''; },
    // Persisted "Respect Monster Quest" switch; it reads the same champion history as VIP.
    toggleMonsterQuest() {
      this.monsterQuest = !this.monsterQuest;
      if (this.monsterQuest) this.ensureVipHistory();
      try { localStorage.setItem(MQ_PREF, this.monsterQuest ? '1' : '0'); } catch { /* private mode — session-only */ }
    },
    // Choose another candidate for this session (the top one clears the choice).
    pickVip(cls) {
      this.vipOverride = this.vipRanked[0] && this.vipRanked[0].cls === cls ? null : cls;
      this.forceOverride = null;
    },
    // Why a candidate, in words: the weaknesses it hits, what blocks it, its win rate.
    vipWhy(c) {
      const parts = [], byEl = {};
      for (const h of c.edge.hits) (byEl[h.element] || (byEl[h.element] = [])).push(h.cls);
      for (const [el, cls] of Object.entries(byEl)) parts.push(`${el} hits their ${cls.join(' and ')}`);
      for (const b of c.edge.blocks) {
        parts.push(b.why === 'innocent' ? `their ${b.cls} shrugs off magic` : `their ${b.cls} ${b.why === 'absorb' ? 'absorbs' : 'nulls'} ${b.element}`);
      }
      parts.push(`${c.base.toFixed(1)}% win rate`);
      return parts.join(' · ');
    },
    // The on-demand reality check: the battle sim for each listed candidate vs the champion.
    async runVipSim() {
      const units = this.champion && this.champion.units;
      if (this.vipSimBusy || !units || !units.length) return;
      this.vipSimBusy = true;
      this.vipSim = {};
      try {
        const { simVipCandidates } = await import('../lib/vipSim.js');
        await simVipCandidates(this.vipList.map((c) => ({ cls: c.cls, skill: c.skill })), units,
          (cls, pct) => { this.vipSim = { ...this.vipSim, [cls]: pct }; });
      } catch (e) {
        console.warn('VIP sim failed', e);
      } finally {
        this.vipSimBusy = false;
      }
    },
    setPool(pool) {
      this.rules.pool = pool;
      this.forceOverride = null;
    },
    // Persisted "Save my hidden skills" switch. Turning it on drops a manual hidden pick.
    toggleHidden() {
      this.saveHidden = !this.saveHidden;
      if (this.saveHidden && isHiddenSkill(this.forceOverride)) this.forceOverride = null;
      try { localStorage.setItem(HIDDEN_PREF, this.saveHidden ? '1' : '0'); } catch { /* private mode — session-only */ }
    },
    orList,
    // Persisted GilgameHeart-exclude toggle for the suggestions.
    toggleGgh() {
      this.excludeGgh = !this.excludeGgh;
      try { localStorage.setItem(GGH_PREF, this.excludeGgh ? '1' : '0'); } catch { /* private mode — session-only */ }
    },
    nameOfToken: nameOf,
    copy(text) {
      try {
        navigator.clipboard.writeText(text);
        this.copied = text;
        setTimeout(() => { if (this.copied === text) this.copied = ''; }, 1500);
      } catch { /* clipboard blocked — the command is still selectable */ }
    },
  },
};
</script>

<style scoped>
.mm-backdrop { background: rgba(0, 0, 0, 0.6); padding: 24px; }
.mm-panel {
  position: relative;
  background: linear-gradient(180deg, var(--panel-top), var(--panel-bottom));
  border: 2px solid rgba(217, 180, 94, 0.22);
  border-radius: 8px;
  color: #fff;
  padding: 22px 26px 24px;
  max-width: 600px;
  width: 100%;
  max-height: 86vh;
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
}
.mm-close {
  position: absolute; top: 8px; right: 10px;
  background: none; border: 0; color: #fff; font-size: 24px; line-height: 1; cursor: pointer; opacity: 0.7;
}
.mm-close:hover { opacity: 1; }
.mm-title { margin: 0 24px 2px 0; font-size: 19px; font-family: var(--display); font-weight: 700; color: var(--brass-hi); }
.mm-sub { margin: 0 0 14px; font-size: 13px; opacity: 0.75; }
.mm-state { font-size: 14px; opacity: 0.85; }

.mm-sec { font-size: 14px; font-weight: bold; margin: 16px 0 8px; border-bottom: 1px solid rgba(255, 255, 255, 0.12); padding-bottom: 5px; }
.mm-sec:first-of-type { margin-top: 4px; }
.mm-sec-sub { font-size: 11px; opacity: 0.55; font-weight: normal; }
.mm-sec-toggle { display: flex; align-items: baseline; gap: 8px; cursor: pointer; user-select: none; }
.mm-sec-toggle:hover { color: var(--brass-hi); }
.mm-toggle { margin-left: auto; font-size: 12px; font-weight: normal; color: var(--brass-hi); white-space: nowrap; }

.mm-mods { display: flex; flex-wrap: wrap; gap: 7px; }
.mm-mod { font-size: 13px; padding: 4px 11px; border-radius: 6px; background: rgba(255, 255, 255, 0.07); border: 1px solid rgba(255, 255, 255, 0.14); }
.mm-mod .mm-mod-v { opacity: 0.82; margin-left: 5px; }
.mm-mod.bonus { border-color: #6aa84f; background: rgba(106, 168, 79, 0.18); }
.mm-mod.cantuse { border-color: #c0392b; background: rgba(192, 57, 43, 0.18); }
.mm-mod.hidden { border-color: #9b59b6; background: rgba(155, 89, 182, 0.18); }

.mm-bonus { font-size: 13px; line-height: 1.5; margin: 0 0 8px; }
.mm-amt { font-weight: bold; color: #6fcf6f; }
.mm-classes { opacity: 0.85; }

.mm-classpick { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 6px; font-size: 12px; margin: 2px 0 12px; }
.mm-class-btn {
  display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px 3px 6px; border-radius: 5px; cursor: pointer;
  background: rgba(0, 0, 0, 0.3); color: #fff; border: 1px solid rgba(255, 255, 255, 0.25);
  font-family: inherit; font-size: 13px;
}
.mm-class-btn:hover { border-color: var(--brass-hi); background: rgba(217, 180, 94, 0.4); }
.mm-class-btn-img { width: 26px; height: 26px; object-fit: contain; image-rendering: pixelated; }
.mm-class-btn-emoji { width: 26px; text-align: center; font-size: 17px; line-height: 26px; }
.mm-class-btn-name { font-weight: bold; }
.mm-class-btn-bonus { font-size: 11px; color: var(--brass-hi); }
.mm-class-btn-caret { opacity: 0.6; font-size: 11px; }
.mm-genderedge { font-size: 12px; margin: 0 0 12px; padding: 6px 10px; border-radius: 5px; background: rgba(217, 180, 94, 0.12); border: 1px solid rgba(217, 180, 94, 0.35); color: var(--ink); line-height: 1.45; }
.mm-genderedge code { background: rgba(0, 0, 0, 0.35); padding: 0 4px; border-radius: 3px; font-family: ui-monospace, Menlo, Consolas, monospace; }
.mm-worstban { background: rgba(224, 160, 48, 0.12); border-color: rgba(224, 160, 48, 0.4); color: #f0d9a8; }
.mm-entry { margin-bottom: 12px; }
.mm-entry-head { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; font-size: 13px; }
.mm-entry-label { font-weight: bold; }
.mm-entry-meta { font-size: 11px; opacity: 0.75; white-space: nowrap; }
.mm-entry-warn { color: #e0a030; }
.mm-cmd-row { display: flex; align-items: stretch; gap: 6px; margin: 3px 0; }
.mm-cmd {
  flex: 1; min-width: 0; padding: 6px 10px; border-radius: 5px;
  background: rgba(0, 0, 0, 0.35); border: 1px solid rgba(255, 255, 255, 0.15);
  font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 13px; color: var(--brass-hi);
  cursor: pointer; overflow-x: auto; white-space: nowrap;
}
.mm-copy {
  flex: 0 0 auto; padding: 0 11px; border-radius: 5px; cursor: pointer;
  background: rgba(217, 180, 94, 0.22); border: 1px solid var(--brass-hi); color: #fff; font-size: 13px; font-weight: bold;
}
.mm-copy:hover { background: rgba(217, 180, 94, 0.3); }
.mm-entry-note { font-size: 11px; opacity: 0.65; line-height: 1.4; }
.mm-entry.poor .mm-cmd { opacity: 0.6; }
.mm-monpanel { margin: 0 0 6px; }
.mm-mon-msg { margin: 0 0 8px; }

/* GilgameHeart-exclude toggle */
.mm-ggh-row { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin: 2px 0 12px; }
.mm-ggh-toggle {
  display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 14px; cursor: pointer;
  background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.22); color: #fff;
  font-family: inherit; font-size: 12px;
}
.mm-ggh-toggle.on { background: rgba(224, 160, 48, 0.18); border-color: #e0a030; color: #f0d9a8; }
.mm-ggh-box { display: inline-flex; align-items: center; justify-content: center; width: 14px; height: 14px; border-radius: 3px; border: 1px solid currentColor; font-size: 11px; line-height: 1; }
.mm-ggh-hint { font-size: 11px; opacity: 0.6; }

.mm-note { font-size: 11px; opacity: 0.55; margin: 12px 0 14px; }
.mm-link { display: inline-block; color: var(--brass-hi); font-size: 13px; font-weight: bold; text-decoration: none; }
.mm-link:hover { text-decoration: underline; }

/* ── Custom builder ── */
.mm-classpick-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin: 2px 0 12px; }
.mm-classpick-row .mm-classpick { margin: 0; }
.mm-custom-toggle {
  flex: 0 0 auto; padding: 5px 12px; border-radius: 5px; cursor: pointer; white-space: nowrap;
  background: rgba(217, 180, 94, 0.12); border: 1px solid var(--brass-hi); color: var(--ink);
  font-family: inherit; font-size: 12px; font-weight: bold;
}
.mm-custom-toggle:hover { background: rgba(217, 180, 94, 0.25); }

.mm-custom { margin-bottom: 8px; }
.mm-custom-head { display: flex; align-items: center; gap: 12px; margin-bottom: 4px; }
.mm-custom-cls { font-size: 16px; font-weight: bold; }
.mm-gender-toggle { display: inline-flex; gap: 4px; }
.mm-gpill {
  padding: 4px 12px; border-radius: 14px; cursor: pointer;
  background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.2); color: #fff;
  font-family: inherit; font-size: 12px;
}
.mm-gpill.on { background: rgba(217, 180, 94, 0.22); border-color: var(--brass-hi); font-weight: bold; }
.mm-gpill:disabled { opacity: 0.3; cursor: not-allowed; }

.mm-custom-sec { font-size: 13px; font-weight: bold; margin: 14px 0 7px; border-bottom: 1px solid rgba(255, 255, 255, 0.1); padding-bottom: 4px; }

.mm-pick-list { display: flex; flex-direction: column; gap: 4px; }
.mm-pick {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 6px 11px; border-radius: 5px; cursor: pointer; text-align: left;
  background: rgba(0, 0, 0, 0.22); border: 1px solid rgba(255, 255, 255, 0.12); color: #fff;
  font-family: inherit; font-size: 13px;
}
.mm-pick:hover { border-color: rgba(217, 180, 94, 0.6); }
.mm-pick.on { background: rgba(217, 180, 94, 0.5); border-color: var(--brass-hi); }
.mm-pick.bribe { border-style: dashed; }
.mm-pick-name { font-weight: bold; }
.mm-pick-meta { display: flex; align-items: center; gap: 8px; font-size: 11px; white-space: nowrap; }
.mm-delta.pos { color: #6fcf6f; }
.mm-delta.neg { color: #e8907f; }
.mm-rar { padding: 1px 6px; border-radius: 8px; font-size: 10px; }
.mm-rar.uncommon { background: rgba(106, 168, 79, 0.25); border: 1px solid #6aa84f; }
.mm-rar.rare { background: rgba(155, 89, 182, 0.25); border: 1px solid #9b59b6; }
.mm-rar.bribe { background: rgba(224, 160, 48, 0.2); border: 1px solid #e0a030; color: #f0d9a8; }
.mm-rar.step { background: rgba(106, 168, 79, 0.2); border: 1px solid #6aa84f; color: #cfe9bf; }
.mm-rar.step.neg { background: rgba(192, 57, 43, 0.2); border: 1px solid #c0392b; color: #f0b8b0; }

.mm-breakdown { margin: 6px 0 0; padding-left: 18px; font-size: 11px; opacity: 0.8; line-height: 1.5; }
.mm-breakdown b { opacity: 1; }

/* ── ⭐ My entry ── */
.mm-remembered { margin-left: 8px; font-size: 11px; color: #f0d9a8; opacity: 0.9; }
.mm-myentry { padding: 10px 12px 10px; border-radius: 7px; background: rgba(224, 160, 48, 0.07); border: 1px solid rgba(224, 160, 48, 0.45); }
.mm-myentry-for { font-weight: normal; opacity: 0.8; }
.mm-rules { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 8px; margin: 8px 0 2px; }
.mm-rule { display: inline-flex; align-items: center; gap: 6px; }
.mm-ggh-toggle:disabled { opacity: 0.4; cursor: not-allowed; }
.mm-pool { display: inline-flex; border: 1px solid rgba(255, 255, 255, 0.22); border-radius: 12px; overflow: hidden; }
.mm-pool-opt { padding: 3px 9px; background: transparent; border: 0; color: var(--ink); cursor: pointer; font-family: inherit; font-size: 11px; }
.mm-pool-opt.on { background: rgba(224, 160, 48, 0.25); color: #f0d9a8; font-weight: bold; }
.mm-quest-off { opacity: 0.75; font-style: italic; }
.mm-pool-note { color: #f0d9a8; }
.mm-vip { margin: 8px 0 4px; padding: 7px 9px; border: 1px solid rgba(224, 184, 58, 0.45); border-radius: 6px; background: rgba(199, 146, 21, 0.08); font-size: 12px; }
.mm-vip-board { margin-left: 6px; color: #e0b83a; font-size: 11.5px; }
.mm-vip-champ { opacity: 0.75; margin: 2px 0 5px; }
.mm-vip-list { list-style: none; margin: 0; padding: 0; }
.mm-vip-list li { display: flex; flex-wrap: wrap; align-items: baseline; gap: 1px 8px; padding: 3px 6px; border-radius: 4px; cursor: pointer; }
.mm-vip-list li:hover { background: rgba(255, 255, 255, 0.06); }
.mm-vip-list li.on { background: rgba(224, 184, 58, 0.18); }
.mm-vip-name { font-weight: bold; }
.mm-vip-tier { font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; padding: 0 6px; border-radius: 7px; background: rgba(155, 89, 182, 0.4); }
.mm-vip-sim { font-weight: bold; color: #7fd08a; }
.mm-vip-why { flex-basis: 100%; opacity: 0.7; font-size: 11.5px; }
.mm-vip-note { margin: 5px 0 0; opacity: 0.75; font-size: 11.5px; }
.mm-vip-msg { opacity: 0.8; margin: 0; }
.mm-vip-simbtn { margin-top: 6px; background: rgba(18, 24, 38, 0.7); color: var(--ink); border: 1px solid var(--brass-edge); border-radius: 4px; font-size: 12px; padding: 3px 10px; cursor: pointer; }
.mm-vip-simbtn:disabled { opacity: 0.6; cursor: wait; }
.mm-finetune { margin-top: 6px; background: none; border: 0; padding: 0; color: var(--brass-hi); cursor: pointer; font-family: inherit; font-size: 12px; font-weight: bold; }
.mm-finetune:hover { text-decoration: underline; }
.mm-myentry .mm-custom { margin: 8px 0 2px; padding-top: 8px; border-top: 1px solid rgba(255, 255, 255, 0.1); }
</style>
